import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiProvider";
import { queryKeys, useNodes, useProjects } from "../../api/queries";
import type { Domain, Placement, Project, Service } from "../../api/types";

/**
 * One row of the fleet-wide Routes overview (ADR-0026 §5, scheduled by
 * ADR-0027 §7 step 5): a single bound hostname, resolved back to the
 * project/service that owns it and the placements currently serving that
 * service. There is no live routing-manifest/cert status to show yet — the
 * manifest itself (ADR-0027 §2b) is generated and pushed to Kelpie by later
 * steps in that ADR's sequence (§7 steps 1-4), not built yet — so this row
 * only ever carries data that already exists server-side: the domain
 * binding and the placement graph, the same two things `DomainsSection` and
 * `PlacementsSection` already render elsewhere in this app. Rendering
 * anything beyond that (a fabricated "active"/"cert issued" status) would be
 * exactly what charter S-15 forbids.
 */
export interface RouteRow {
  domain: Domain;
  project: Project;
  service: Service;
  placements: Placement[];
}

export interface RouteRowsResult {
  rows: RouteRow[];
  isPending: boolean;
  isError: boolean;
  error: unknown;
  /**
   * True when the routes themselves (projects/services/domains) loaded fine
   * but resolving placements for one or more services failed — the "which
   * nodes serve this" column degrades to "unknown" rather than the whole
   * page failing, mirroring `HostDetailPage`'s `indexIsError` precedent for
   * a secondary, non-blocking resolution failure.
   */
  placementsDegraded: boolean;
  /** Resolves a placement's node id to its human-readable name, degrading to
   * the raw id if the node isn't in the (best-effort, one-page) node list —
   * the same "resolve to a name, fall back to the id" pattern
   * `EnvironmentDetailPage` uses for its source-environment reference. */
  nodeName: (nodeId: string) => string;
  refetch: () => void;
}

/**
 * Fans out from the fleet's projects to every service's domain bindings and
 * placements — the fleet-wide composition of what `DomainsSection` and
 * `PlacementsSection` already fetch per-service on the service detail page.
 * Capped to one page of projects/services (`limit: 100`, matching the
 * identical cap `useServiceProjectIndex` in `HostDetailPage.tsx` already
 * uses for the same shape of fan-out) — an accepted pre-alpha N+1 per the PR
 * brief; a fleet-wide read model would remove it if project/service counts
 * grow.
 */
export function useRouteRows(): RouteRowsResult {
  const client = useApiClient();

  const {
    data: projectsPage,
    isPending: projectsPending,
    isError: projectsIsError,
    error: projectsError,
    refetch: refetchProjects,
  } = useProjects({ limit: 100 });
  const projects = useMemo(() => projectsPage?.items ?? [], [projectsPage]);

  // Best-effort node-name resolution for the "Nodes" column — same cap as
  // every other reference list in this fan-out (limit: 100). A miss (node
  // list still loading, or a node this page doesn't know about) just falls
  // back to the raw id rather than blocking or erroring the whole page.
  const { data: nodesPage } = useNodes({ limit: 100 });
  const nodeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of nodesPage?.items ?? []) map.set(node.id, node.name);
    return map;
  }, [nodesPage]);

  const serviceQueries = useQueries({
    queries: projects.map((project) => ({
      queryKey: queryKeys.services(project.id),
      queryFn: () => client.listServices({ projectId: project.id, limit: 100 }),
      enabled: Boolean(project.id),
    })),
  });

  const serviceEntries = useMemo(() => {
    const entries: { project: Project; service: Service }[] = [];
    projects.forEach((project, i) => {
      const services = serviceQueries[i]?.data?.items ?? [];
      services.forEach((service) => entries.push({ project, service }));
    });
    return entries;
  }, [projects, serviceQueries]);

  const domainQueries = useQueries({
    queries: serviceEntries.map(({ project, service }) => ({
      queryKey: queryKeys.domains(project.id, service.id),
      queryFn: () => client.listDomains(project.id, service.id, { limit: 100 }),
      enabled: true,
    })),
  });

  const placementQueries = useQueries({
    queries: serviceEntries.map(({ project, service }) => ({
      queryKey: queryKeys.placements(project.id, service.id),
      queryFn: () => client.listPlacements({ projectId: project.id, serviceId: service.id, limit: 100 }),
      enabled: true,
    })),
  });

  const servicesPending = serviceQueries.some((q) => q.isPending);
  const servicesIsError = serviceQueries.some((q) => q.isError);
  const domainsPending = domainQueries.some((q) => q.isPending);
  const domainsIsError = domainQueries.some((q) => q.isError);
  const placementsPending = placementQueries.some((q) => q.isPending);
  const placementsIsError = placementQueries.some((q) => q.isError);

  const rows: RouteRow[] = useMemo(() => {
    const built: RouteRow[] = [];
    serviceEntries.forEach(({ project, service }, i) => {
      const domains = domainQueries[i]?.data?.items ?? [];
      const placements = placementQueries[i]?.data?.items ?? [];
      domains.forEach((domain) => built.push({ domain, project, service, placements }));
    });
    return built;
  }, [serviceEntries, domainQueries, placementQueries]);

  return {
    rows,
    isPending: projectsPending || servicesPending || domainsPending || placementsPending,
    isError: projectsIsError || servicesIsError || domainsIsError,
    error: projectsError ?? serviceQueries.find((q) => q.error)?.error ?? domainQueries.find((q) => q.error)?.error,
    placementsDegraded: placementsIsError,
    nodeName: (nodeId: string) => nodeNameById.get(nodeId) ?? nodeId,
    refetch: () => {
      void refetchProjects();
      serviceQueries.forEach((q) => void q.refetch());
      domainQueries.forEach((q) => void q.refetch());
      placementQueries.forEach((q) => void q.refetch());
    },
  };
}
